import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { btnCls, cget, cpost, cput, errText, gbp, inputCls, mcomp, mget, mput } from "./api";

const Num = ({ label, v, set, step = "0.01", testId }) => (
  <label className="flex items-center justify-between gap-2 text-sm">{label}
    <input type="number" step={step} className={`${inputCls} w-28`} value={v ?? ""} onChange={(e) => set(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testId} />
  </label>
);

const CommissionSettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => { cget("/settings").then(setS).catch(() => {}); }, []);
  if (!s) return null;
  const save = () => cput("/settings", s).then((d) => { setS(d); toast.success("Settings saved"); }).catch((e) => toast.error(errText(e)));
  const setCost = (p, k, v) => setS({ ...s, products: { ...s.products, [p]: { ...s.products[p], costs: { ...s.products[p].costs, [k]: v } } } });
  return (
    <section className="rounded-2xl border border-[#e5e0d3] bg-white p-4" data-testid="commission-settings">
      <h3 className="text-lg">Commission + payout</h3>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <Num label="Affiliate % (0.25 = 25%)" v={s.affiliate_pct} set={(v) => setS({ ...s, affiliate_pct: v })} testId="settings-affiliate-pct" />
        <Num label="Partner % (0.05 = 5%)" v={s.partner_pct} set={(v) => setS({ ...s, partner_pct: v })} testId="settings-partner-pct" />
        <Num label="Minimum payout £" v={s.min_payout_gbp} set={(v) => setS({ ...s, min_payout_gbp: v })} testId="settings-min-payout" />
        <Num label="VAT fallback (when Stripe gives none)" v={s.customer_vat_fallback} set={(v) => setS({ ...s, customer_vat_fallback: v })} />
        <Num label="Stripe % of gross" v={s.stripe_pct} step="0.001" set={(v) => setS({ ...s, stripe_pct: v })} />
        <Num label="Stripe fixed £ per payment" v={s.stripe_fixed_gbp} set={(v) => setS({ ...s, stripe_fixed_gbp: v })} />
        <Num label="Chargeback fee £" v={s.chargeback_fee_gbp} set={(v) => setS({ ...s, chargeback_fee_gbp: v })} />
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {["name", "address", "vat_number"].map((k) => (
          <input key={k} className={inputCls} placeholder={`Company ${k.replace("_", " ")} (for invoices)`} value={s.company?.[k] || ""}
            onChange={(e) => setS({ ...s, company: { ...s.company, [k]: e.target.value } })} data-testid={`settings-company-${k}`} />
        ))}
      </div>
      <h4 className="mt-4 text-sm font-medium">Cost assumptions per payment (£ per month; one-offs per sale)</h4>
      {Object.entries(s.products).map(([p, pc]) => (
        <div key={p} className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid={`settings-costs-${p}`}>
          <span className="w-32 font-medium">{pc.label}</span>
          {Object.entries(pc.costs || {}).map(([k, v]) => (
            <label key={k} className="flex items-center gap-1">{k}<input type="number" step="0.01" className={`${inputCls} w-20`} value={v} onChange={(e) => setCost(p, k, Number(e.target.value))} /></label>
          ))}
          <label className="flex items-center gap-1">refund allowance<input type="number" step="0.01" className={`${inputCls} w-20`} value={pc.refund_allowance}
            onChange={(e) => setS({ ...s, products: { ...s.products, [p]: { ...pc, refund_allowance: Number(e.target.value) } } })} /></label>
        </div>
      ))}
      <button type="button" className={`${btnCls} mt-4`} onClick={save} data-testid="settings-save-btn">Save settings</button>
    </section>
  );
};

const MembershipSettings = () => {
  const [c, setC] = useState(null);
  useEffect(() => { mget().then(setC).catch(() => {}); }, []);
  if (!c) return null;
  const save = () => mput({ ...c, allowance_confirmed: true }).then((d) => { setC(d); toast.success("Membership levels saved"); }).catch((e) => toast.error(errText(e)));
  const setA = (k, f, v) => setC({ ...c, areas: { ...c.areas, [k]: { ...c.areas[k], [f]: v } } });
  return (
    <section className="rounded-2xl border border-[#e5e0d3] bg-white p-4" data-testid="membership-settings">
      <h3 className="text-lg">Membership levels + Coach allowance</h3>
      {!c.allowance_confirmed && <p className="mt-1 text-xs text-[#a0522d]" data-testid="membership-allowance-warning">Coach minutes are not confirmed yet. Save to confirm your chosen allowance.</p>}
      {Object.entries(c.areas).map(([k, a]) => (
        <div key={k} className="mt-3 grid gap-2 sm:grid-cols-3" data-testid={`membership-area-${k}`}>
          <input className={inputCls} value={a.label} onChange={(e) => setA(k, "label", e.target.value)} />
          <Num label="Price (pence)" step="1" v={a.price_pence} set={(v) => setA(k, "price_pence", v)} testId={`area-price-${k}`} />
          <Num label="Coach minutes / month" step="1" v={a.coach_minutes} set={(v) => setA(k, "coach_minutes", v)} testId={`area-minutes-${k}`} />
        </div>
      ))}
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <Num label="Minutes per Coach reply" v={c.minutes_per_exchange} set={(v) => setC({ ...c, minutes_per_exchange: v })} testId="coach-minutes-per-exchange" />
        <Num label="Top-up minutes" step="1" v={c.topup.minutes} set={(v) => setC({ ...c, topup: { ...c.topup, minutes: v } })} testId="topup-minutes" />
        <Num label="Top-up price (pence)" step="1" v={c.topup.price_pence} set={(v) => setC({ ...c, topup: { ...c.topup, price_pence: v } })} testId="topup-price" />
      </div>
      <p className="mt-2 text-xs text-[#6b6b6b]">Included minutes reset each billing month and don't roll over. Bought top-up minutes stay until used.</p>
      <button type="button" className={`${btnCls} mt-3`} onClick={save} data-testid="membership-save-btn">Save membership levels</button>
    </section>
  );
};

const Calculator = () => {
  const [f, setF] = useState({ product: "core", gross: 6.99, months: 1 });
  const [r, setR] = useState(null);
  const run = () => cpost("/calculator", f).then(setR).catch((e) => toast.error(errText(e)));
  return (
    <section className="rounded-2xl border border-[#e5e0d3] bg-white p-4" data-testid="commission-calculator">
      <h3 className="text-lg">Worked example calculator</h3>
      <div className="mt-2 flex flex-wrap gap-2">
        <select className={inputCls} value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })} data-testid="calc-product">
          {["core", "golf", "gym", "voice_premium", "atelier"].map((p) => <option key={p}>{p}</option>)}</select>
        <input type="number" step="0.01" className={`${inputCls} w-24`} value={f.gross} onChange={(e) => setF({ ...f, gross: Number(e.target.value) })} data-testid="calc-gross" />
        <button type="button" className={btnCls} onClick={run} data-testid="calc-run-btn">Calculate</button>
      </div>
      {r && (
        <table className="mt-3 w-full text-right text-xs" data-testid="calc-result">
          <thead className="text-[#6b6b6b]"><tr><th className="text-left">Scenario</th><th>VAT</th><th>Net</th><th>Affiliate</th><th>Partner</th><th>Principals</th><th>Stripe</th><th>Costs</th><th>Contribution</th><th>After refund allowance</th></tr></thead>
          <tbody>{Object.entries(r.scenarios).map(([k, c]) => (
            <tr key={k} className="border-t border-[#eee9dc]"><td className="py-1 text-left">{k.replace(/_/g, " ")}</td><td>{gbp(c.tax)}</td><td>{gbp(c.net)}</td><td>{gbp(c.affiliate.amount)}</td>
              <td>{gbp(c.partner.amount)}</td><td>{gbp(c.principal_total)}</td><td>{gbp(c.processing)}</td><td>{gbp(c.operating_total)}</td><td>{gbp(c.contribution)}</td><td className="font-medium">{gbp(c.after_refund_allowance)}</td></tr>
          ))}</tbody>
        </table>
      )}
      {r && !r.network_used && <p className="mt-2 text-xs text-[#a0522d]">No active network yet, so Network Principal amounts show as £0.</p>}
    </section>
  );
};

const CompMember = () => {
  const [email, setEmail] = useState("");
  const [areas, setAreas] = useState(["golf", "gym"]);
  const go = () => mcomp({ email, premium: true, areas })
    .then((d) => { toast.success(`${d.email} now has complimentary Premium`); setEmail(""); }).catch((e) => toast.error(errText(e)));
  return (
    <section className="rounded-2xl border border-[#e5e0d3] bg-white p-4" data-testid="comp-member">
      <h3 className="text-lg">Complimentary Premium member</h3>
      <p className="text-xs text-[#6b6b6b]">Full Premium with the normal monthly allowances (not unlimited like team accounts).</p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <input className={inputCls} placeholder="their account email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="comp-email" />
        {["golf", "gym"].map((a) => (
          <label key={a} className="flex items-center gap-1 text-sm"><input type="checkbox" checked={areas.includes(a)}
            onChange={(e) => setAreas(e.target.checked ? [...areas, a] : areas.filter((x) => x !== a))} /> + {a}</label>
        ))}
        <button type="button" className={btnCls} onClick={go} data-testid="comp-save-btn">Give Premium</button>
      </div>
    </section>
  );
};

export const SettingsTab = () => (
  <div className="space-y-5" data-testid="commissions-settings-tab"><Calculator /><CompMember /><CommissionSettings /><MembershipSettings /></div>
);
